<script setup lang="ts">
import { reactive, ref, watch, defineEmits, defineProps, defineExpose, onMounted } from 'vue'
import moment from 'moment'

interface Collectivite {
  id: number
  nom: string
}

interface PersonalInfosPayload {
  nom: string | null
  prenom: string | null
  date_naissance: string | null
  age: number
  date_engagement: string | null
  anciennete: number
  collectivite_id: number | null
  salaire: string
  quotite_cessible_pourcent: string
  quotite_cessible_montant: string
  telephone: string | null
  email: string | null
}

const props = defineProps<{
  collectivites: Collectivite[]
  defaultCollectiviteId?: number | null
  hideSubmitButton?: boolean
  hideCollectiviteField?: boolean
  initialData?: Partial<PersonalInfosPayload> | null
}>()

const formData = reactive<PersonalInfosPayload>({
  nom: '',
  prenom: '',
  date_naissance: '',
  age: 0,
  date_engagement: '',
  anciennete: 0,
  collectivite_id: null,
  salaire: '',
  quotite_cessible_pourcent: '',
  quotite_cessible_montant: '',
  telephone: '',
  email: ''
})

const emit = defineEmits<{
  (e: 'saved', payload: PersonalInfosPayload): void
}>()

const errorMessage = ref('')
const isFormValid = ref(false)

const errors = reactive<Record<string, string>>({
  nom: '',
  prenom: '',
  date_naissance: '',
  age: '',
  date_engagement: '',
  anciennete: '',
  collectivite_id: '',
  salaire: '',
  quotite_cessible_pourcent: '',
  quotite_cessible_montant: '',
  telephone: '',
  email: ''
})

function recomputeAge() {
  if (!formData.date_naissance) { formData.age = 0; return }
  const birthDate = moment(formData.date_naissance)
  if (birthDate.isValid()) formData.age = moment().diff(birthDate, 'years')
}

function recomputeAnciennete() {
  if (!formData.date_engagement) { formData.anciennete = 0; return }
  const engagementDate = moment(formData.date_engagement)
  if (engagementDate.isValid()) formData.anciennete = moment().diff(engagementDate, 'years')
}

function onDateNaissanceChange(e: Event) {
  formData.date_naissance = (e.target as HTMLInputElement).value
  recomputeAge()
}

function onDateEngagementChange(e: Event) {
  formData.date_engagement = (e.target as HTMLInputElement).value
  recomputeAnciennete()
}

// Fallback : watcher pour binding programmatique
watch(() => formData.date_naissance, () => recomputeAge())
watch(() => formData.date_engagement, () => recomputeAnciennete())

// Pré-sélectionner la collectivité si fournie
onMounted(() => {
  if (props.defaultCollectiviteId && !formData.collectivite_id) {
    // Vérifier que la collectivité existe dans la liste
    const collectiviteExists = props.collectivites.some(c => c.id === props.defaultCollectiviteId)
    if (collectiviteExists) {
      formData.collectivite_id = props.defaultCollectiviteId
    }
  }
  if (props.initialData) {
    applyInitialData(props.initialData)
  }
})

watch(
  () => props.initialData,
  (data) => {
    if (data) applyInitialData(data)
  },
  { deep: true }
)

function applyInitialData(data: Partial<PersonalInfosPayload>) {
  if (data.nom !== undefined) formData.nom = data.nom
  if (data.prenom !== undefined) formData.prenom = data.prenom
  if (data.date_naissance !== undefined) formData.date_naissance = data.date_naissance
  if (data.age !== undefined) formData.age = data.age
  if (data.date_engagement !== undefined) formData.date_engagement = data.date_engagement
  if (data.anciennete !== undefined) formData.anciennete = data.anciennete
  if (data.collectivite_id !== undefined) formData.collectivite_id = data.collectivite_id
  if (data.salaire !== undefined) formData.salaire = data.salaire
  if (data.quotite_cessible_pourcent !== undefined) formData.quotite_cessible_pourcent = data.quotite_cessible_pourcent
  if (data.quotite_cessible_montant !== undefined) formData.quotite_cessible_montant = data.quotite_cessible_montant
  if (data.telephone !== undefined) formData.telephone = data.telephone
  if (data.email !== undefined) formData.email = data.email
}

// Écouter les changements de la prop defaultCollectiviteId
watch(
  () => props.defaultCollectiviteId,
  (newValue) => {
    if (newValue && !formData.collectivite_id) {
      const collectiviteExists = props.collectivites.some(c => c.id === newValue)
      if (collectiviteExists) {
        formData.collectivite_id = newValue
      }
    }
  }
)

function validate() {
  let isValid = true
  Object.keys(errors).forEach(key => {
    errors[key] = ''
  })

  if (!formData.nom) {
    errors.nom = 'Le nom est requis'
    isValid = false
  }
  if (!formData.prenom) {
    errors.prenom = 'Le prénom est requis'
    isValid = false
  }
  if (!formData.date_naissance) {
    errors.date_naissance = 'La date de naissance est requise'
    isValid = false
  } else if (!moment(formData.date_naissance).isValid()) {
    errors.date_naissance = 'Le format de la date de naissance est invalide'
    isValid = false
  }
  if (!formData.age || isNaN(formData.age) || formData.age <= 0) {
    errors.age = "L'âge est requis"
    isValid = false
  }
  if (!formData.date_engagement) {
    errors.date_engagement = "La date d'engagement est requise"
    isValid = false
  } else if (!moment(formData.date_engagement).isValid()) {
    errors.date_engagement = "Le format de la date d'engagement est invalide"
    isValid = false
  }
  if (formData.anciennete === null || formData.anciennete < 0 || isNaN(formData.anciennete)) {
    errors.anciennete = "L'ancienneté est requise"
    isValid = false
  }
  if (!formData.collectivite_id) {
    errors.collectivite_id = 'La collectivité est requise'
    isValid = false
  }
  // Nettoyer le salaire pour la validation (retirer espaces et virgules)
  const salaireCleaned = formData.salaire.replace(/\s/g, '').replace(/,/g, '')
  const salaireValue = parseFloat(salaireCleaned)
  if (!formData.salaire || isNaN(salaireValue) || salaireValue <= 0) {
    errors.salaire = 'Le salaire est requis'
    isValid = false
  }
  
  // Nettoyer la quotité pour la validation (retirer espaces et virgules)
  const quotiteCleaned = formData.quotite_cessible_pourcent.replace(/\s/g, '').replace(/,/g, '')
  const quotitePourcent = parseFloat(quotiteCleaned)
  if (
    !formData.quotite_cessible_pourcent ||
    isNaN(quotitePourcent) ||
    quotitePourcent <= 0 ||
    quotitePourcent > 100
  ) {
    errors.quotite_cessible_pourcent = 'La quotité cessible en pourcentage est invalide (doit être entre 0 et 100)'
    isValid = false
  }
  
  // Nettoyer le montant pour la validation (retirer espaces et virgules)
  const montantCleaned = formData.quotite_cessible_montant.replace(/\s/g, '').replace(/,/g, '')
  const montantValue = parseFloat(montantCleaned)
  if (
    !formData.quotite_cessible_montant ||
    isNaN(montantValue) ||
    montantValue <= 0
  ) {
    errors.quotite_cessible_montant = 'La quotité cessible en montant est invalide'
    isValid = false
  }
  if (!formData.telephone) {
    errors.telephone = 'Le téléphone est requis'
    isValid = false
  }
  if (!formData.email) {
    errors.email = "L'email est requis"
    isValid = false
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = "Le format de l'email est invalide"
    isValid = false
  }

  isFormValid.value = isValid
  return isValid
}

async function onSubmit() {
  if (!validate()) {
    return
  }
  
  // Nettoyer les valeurs avant l'envoi (retirer espaces et virgules)
  const cleanedData = {
    ...formData,
    salaire: formData.salaire.replace(/\s/g, '').replace(/,/g, ''),
    quotite_cessible_pourcent: formData.quotite_cessible_pourcent.replace(/\s/g, '').replace(/,/g, ''),
    quotite_cessible_montant: formData.quotite_cessible_montant.replace(/\s/g, '').replace(/,/g, '')
  }
  
  emit('saved', cleanedData)
}

function formatSalaireInput(event: Event) {
  const input = event.target as HTMLInputElement
  // Ne garder que les chiffres (bloquer tous les caractères non numériques)
  const value = input.value.replace(/[^\d]/g, '')
  // Mettre à jour la valeur dans le modèle ET dans l'input
  formData.salaire = value
  input.value = value
  calculateQuotiteCessible()
}

function formatQuotiteInput(event: Event) {
  const input = event.target as HTMLInputElement
  // Ne garder que les chiffres et un point/virgule pour les décimales
  let value = input.value.replace(/[^\d.,]/g, '')
  // Remplacer les virgules par des points
  value = value.replace(/,/g, '.')
  // Ne garder qu'un seul point
  const parts = value.split('.')
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('')
  }
  // Mettre à jour la valeur dans le modèle ET dans l'input
  formData.quotite_cessible_pourcent = value
  input.value = value
  calculateQuotiteCessibleValue()
}

function onQuotiteKeyPress(event: KeyboardEvent) {
  // Autoriser les chiffres, le point, la virgule, et les touches de contrôle (backspace, delete, etc.)
  const allowedKeys = /[0-9.,]/
  const controlKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
  
  if (!allowedKeys.test(event.key) && !controlKeys.includes(event.key) && !event.ctrlKey && !event.metaKey) {
    event.preventDefault()
  }
}

function onQuotitePaste(event: ClipboardEvent) {
  event.preventDefault()
  const pasted = event.clipboardData?.getData('text') ?? ''
  // Ne garder que les chiffres, points et virgules
  let value = pasted.replace(/[^\d.,]/g, '')
  // Remplacer les virgules par des points
  value = value.replace(/,/g, '.')
  // Ne garder qu'un seul point
  const parts = value.split('.')
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('')
  }
  formData.quotite_cessible_pourcent = value
  calculateQuotiteCessibleValue()
}

function calculateQuotiteCessible() {
  if (formData.salaire && formData.quotite_cessible_pourcent) {
    calculateQuotiteCessibleValue()
  }
}

function calculateQuotiteCessibleValue() {
  if (formData.salaire && formData.quotite_cessible_pourcent) {
    // Nettoyer les espaces et virgules du salaire
    const salaire = parseFloat(formData.salaire.replace(/\s/g, '').replace(/,/g, ''))
    // Nettoyer la quotité (remplacer virgule par point)
    const quotiteCleaned = formData.quotite_cessible_pourcent.replace(/\s/g, '').replace(/,/g, '.')
    const quotite = parseFloat(quotiteCleaned)
    
    if (!isNaN(salaire) && !isNaN(quotite)) {
      const value = (salaire * quotite) / 100
      formData.quotite_cessible_montant = Math.round(value).toString()
    }
  }
}

defineExpose({ formData, validate, onSubmit })
</script>

<template>
  <form class="s20 form" @submit.prevent="onSubmit">
    <div class="container-full-1">
      <h3 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
        <img src="/public/icons/icon-info-perso.svg" class="size-base" alt="" />
        Informations personnelles
      </h3>

      <div class="form-field-floating reactive active my-5">
        <input type="text" class="form-input" v-model="formData.nom" name="nom" id="nom" placeholder="" />
        <label for="nom">Nom <b class="color-danger">*</b></label>
        <span class="text-red" v-if="errors.nom">{{ errors.nom }}</span>
      </div>
      <div class="form-field-floating reactive my-5">
        <input type="text" class="form-input" v-model="formData.prenom" name="prenom" id="prenom" placeholder="" />
        <label for="prenom">Prénoms <b class="color-danger">*</b></label>
        <span class="text-red" v-if="errors.prenom">{{ errors.prenom }}</span>
      </div>

      <div class="form-field-split my-5">
        <div class="form-field-floating reactive half">
          <input
            type="date"
            class="form-input"
            :value="formData.date_naissance"
            @input="onDateNaissanceChange"
            @change="onDateNaissanceChange"
            name="date_naissance"
            id="date_naissance"
            placeholder=""
          />
          <label for="date_naissance">Date de naissance <b class="color-danger">*</b></label>
          <span class="text-red" v-if="errors.date_naissance">{{ errors.date_naissance }}</span>
        </div>
        <hr class="hr" />
        <div class="form-field-floating reactive half">
          <input type="text" class="form-input" v-model="formData.age" name="age" id="age" placeholder="" readonly />
          <label for="age">Age <b class="color-danger">*</b></label>
          <span class="text-red" v-if="errors.age">{{ errors.age }}</span>
        </div>
      </div>

      <div class="form-field-split my-5">
        <div class="form-field-floating reactive half">
          <input
            type="date"
            class="form-input"
            :value="formData.date_engagement"
            @input="onDateEngagementChange"
            @change="onDateEngagementChange"
            name="date_engagement"
            id="date_engagement"
            placeholder=""
          />
          <label for="date_engagement">Date d'engagement <b class="color-danger">*</b></label>
          <span class="text-red" v-if="errors.date_engagement">{{ errors.date_engagement }}</span>
        </div>
        <hr class="hr" />
        <div class="form-field-floating reactive half">
          <input
            type="text"
            class="form-input"
            v-model="formData.anciennete"
            name="anciennete"
            id="anciennete"
            placeholder=""
          />
          <label for="anciennete">Ancienneté <b class="color-danger">*</b></label>
          <span class="text-red" v-if="errors.anciennete">{{ errors.anciennete }}</span>
        </div>
      </div>

      <div v-if="!props.hideCollectiviteField" class="form-field-floating full my-5">
        <select class="form-input-select" v-model="formData.collectivite_id" name="collectivite_id" id="collectivite_id">
          <option :value="null">Sélectionnez votre collectivité</option>
          <option :value="collectivite.id" v-for="collectivite in props.collectivites" :key="collectivite.id">
            {{ collectivite.nom }}
          </option>
        </select>
        <label for="collectivite_id">Votre collectivité <b class="color-danger">*</b></label>
        <span class="text-red" v-if="errors.collectivite_id">{{ errors.collectivite_id }}</span>
      </div>
    </div>

    <div class="clear-both h-5"></div>

    <div class="container-full-1">
      <h3 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
        <img src="/public/icons/icon-engagement.svg" class="size-base" alt="" />
        Engagement
      </h3>
      <div class="form-field-floating reactive active my-5">
        <input
          type="text"
          class="form-input"
          v-model="formData.salaire"
          name="salaire"
          id="salaire"
          @input="formatSalaireInput"
          @keypress="(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault() }"
          @paste="(e) => { e.preventDefault(); const pasted = (e.clipboardData || (window as any).clipboardData).getData('text'); const numbers = pasted.replace(/[^\d]/g, ''); formData.salaire = numbers; calculateQuotiteCessible(); }"
          placeholder=""
        />
        <label for="salaire">Salaire brut <b class="color-danger">*</b></label>
        <span>F&nbsp;CFA</span>
        <span class="text-red" v-if="errors.salaire">{{ errors.salaire }}</span>
      </div>
      <div class="form-field-split active my-5">
        <div class="form-field-floating reactive half">
          <input
            type="text"
            class="form-input"
            v-model="formData.quotite_cessible_pourcent"
            name="quotite_cessible_pourcent"
            id="quotite_cessible_pourcent"
            @input="formatQuotiteInput"
            @keypress="onQuotiteKeyPress"
            @paste="onQuotitePaste"
            placeholder=""
          />
          <label for="quotite_cessible_pourcent">Quotité cessible max <b class="color-danger">*</b></label>
          <span>%</span>
          <span class="text-red" v-if="errors.quotite_cessible_pourcent">{{ errors.quotite_cessible_pourcent }}</span>
        </div>
        <hr class="hr" />
        <div class="form-field-floating reactive half">
          <input
            type="text"
            class="form-input"
            v-model="formData.quotite_cessible_montant"
            name="quotite_cessible_montant"
            id="quotite_cessible_montant"
            placeholder=""
            readonly
          />
          <label for="quotite_cessible_montant">Valeur <b class="color-danger">*</b></label>
          <span>F&nbsp;CFA</span>
          <span class="text-red" v-if="errors.quotite_cessible_montant">{{ errors.quotite_cessible_montant }}</span>
        </div>
      </div>

      <div class="form-field-split active my-5">
        <div class="form-field-floating reactive half">
          <input type="text" class="form-input" v-model="formData.telephone" name="telephone" id="telephone" placeholder="" />
          <label for="telephone">Numéro de téléphone <b class="color-danger">*</b></label>
          <span class="text-red" v-if="errors.telephone">{{ errors.telephone }}</span>
        </div>
        <hr class="hr" />
        <div class="form-field-floating reactive half">
          <input type="text" class="form-input" v-model="formData.email" name="email" id="email" placeholder="" />
          <label for="email">Adresse Email <b class="color-danger">*</b></label>
          <span class="text-red" v-if="errors.email">{{ errors.email }}</span>
        </div>
      </div>

      <button v-if="!props.hideSubmitButton" type="button" class="btn flex-center mt-8 mb-7" @click="onSubmit">
        <span>Suivant</span>
        <img src="/public/icons/icon-go.svg" class="btn-icon" alt="" />
      </button>
      <p class="text-red" v-if="errorMessage">{{ errorMessage }}</p>
    </div>
  </form>
</template>

